import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaClipboardList, 
  FaBullhorn,
  // FaClipboardCheck,
  FaUsers,
  FaChartLine,
  FaCheck,
  FaDesktop,
  FaChartBar,
  FaBroom,
  FaCalendarCheck,
  FaIdBadge
} from 'react-icons/fa';
import { IconType } from 'react-icons';
import './Features.scss';

interface Feature {
  id: string;
  title: string;
  description: string;
  icon: IconType;
  rgb: string;
  className: string;
  details: string[];
}

const features: Feature[] = [
  {
    id: 'requests',
    title: 'Умные заявки',
    description: 'Структурированная система подачи обращений, которая исключает фактор забывчивости. Каждая задача имеет трекинг статуса.',
    // @ts-ignore
    icon: FaClipboardList,
    rgb: '14, 165, 233', // Sky blue
    className: 'feature-bento--large',
    details: [
      'Автоматическая маршрутизация на отделы',
      'Назначение и учет ответственных',
      'Отслеживание статуса в реальном времени'
    ]
  },
  {
    id: 'statistics',
    title: 'Сквозная аналитика',
    description: 'Общая статистика по периодам и разделам. Довольность сотрудников (сбор фидбека), расчет скорости отработки и принятия заявок в работу.',
    // @ts-ignore
    icon: FaChartLine,
    rgb: '236, 72, 153', // Pink
    className: 'feature-bento--tall',
    details: [
      'Визуализация узких мест',
      'Скорость решения заявок',
      'Сбор данных для KPI'
    ]
  },
  {
    id: 'templates',
    title: 'Ролевые модели',
    description: 'Создавайте кастомные роли, гибко настраивайте права, назначайте ответственных менеджеров за конкретные разделы и делегируйте доступ сотрудникам.',
    // @ts-ignore
    icon: FaUsers,
    rgb: '168, 85, 247', // Purple
    className: '',
    details: [
      'Иерархия контроля',
      'Гибкие права доступа'
    ]
  },
  {
    id: 'announcements',
    title: 'Сеть объявлений',
    description: 'Таргетированные объявления по созданным ролям. Подробные отчеты о прочтении, история активности и автоматические push-напоминания.',
    // @ts-ignore
    icon: FaBullhorn,
    rgb: '245, 158, 11', // Amber
    className: '',
    details: [
      'Telegram-интеграция',
      'История активности'
    ]
  },
  {
    id: 'booking',
    title: 'Бронирование помещений',
    description: 'Интерактивная карта этажей с кабинетами и переговорными. Свободное время видно сразу, а бронь при необходимости уходит на согласование.',
    // @ts-ignore
    icon: FaCalendarCheck,
    rgb: '99, 102, 241', // Indigo
    className: '',
    details: [
      'Карта этажей и расписание',
      'Согласование брони'
    ]
  },
  {
    id: 'rounds',
    title: 'Цифровые обходы',
    description: 'Техперсонал сканирует QR-код на точке и отмечает уборку с фото прямо в Telegram. Вы видите, какие зоны убраны по графику, а какие просрочены.',
    // @ts-ignore
    icon: FaBroom,
    rgb: '20, 184, 166', // Teal
    className: '',
    details: [
      'QR-метки на каждой точке',
      'Контроль просрочек',
      'Фотоотчёты и экспорт журнала'
    ]
  },
  // Цифровые документы временно скрыты с лендинга.
  // {
  //   id: 'clearance',
  //   title: 'Цифровые документы',
  //   description: 'Отказ от бумажных обходных листов и актов. Все согласования происходят онлайн с электронными подписями.',
  //   // @ts-ignore
  //   icon: FaClipboardCheck,
  //   rgb: '16, 185, 129', // Green
  //   className: 'feature-bento--large',
  //   details: [
  //     'Электронные подписи и валидация',
  //     'Экспорт в один клик (PDF, Excel)',
  //     'Надежный архив документов'
  //   ]
  // },
  {
    id: 'guests',
    title: 'Гостевые пропуска',
    description: 'Запрос пропуска, согласование ответственным, отметка входа и выхода. Всегда известно, кто сейчас в здании и кто его пригласил.',
    // @ts-ignore
    icon: FaIdBadge,
    rgb: '244, 63, 94', // Rose
    className: '',
    details: [
      'Одобрение пропусков',
      'Журнал входа и выхода'
    ]
  }
];

const Features: React.FC = () => {
  const renderIcon = (IconComponent: IconType, props: any = {}) => {
    // @ts-ignore
    return <IconComponent {...props} />;
  };
  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <section id="features" className="features section">
      <motion.div 
        className="section-heading"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <span className="section-badge">Возможности</span>
        <div className="section-heading__title-wrapper">
          <div className="section-heading__icon">
            {/* @ts-ignore */}
            <FaDesktop />
          </div>
          <h2 className="section-heading__title">Единое окно контроля</h2>
        </div>
        <p className="section-heading__subtitle">
          Полный набор структурированных инструментов для оптимизации процессов вашей компании.
        </p>
      </motion.div>

      <motion.div 
        className="features__grid"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {features.map((feature) => (
          <motion.div 
            key={feature.id}
            className={`feature-bento ${feature.className}`}
            style={{ '--feature-rgb': feature.rgb } as React.CSSProperties}
            variants={itemVariants}
          >
            <div className="feature-bento__bg-glow"></div>
            <div className="feature-bento__content">
              <div className="feature-bento__icon">
                {renderIcon(feature.icon)}
              </div>
              <h3 className="feature-bento__title">{feature.title}</h3>
              <p className="feature-bento__description">{feature.description}</p>
              
              <div className="feature-bento__details">
                {feature.details.map((detail, idx) => (
                  <div key={idx} className="detail-item">
                    {/* @ts-ignore */}
                    <FaCheck className="detail-item__check" />
                    <span className="detail-item__text">{detail}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <div className="features__stats">
        <motion.div 
          className="section-heading"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="section-badge">Аналитика</span>
          <div className="section-heading__title-wrapper">
            <div className="section-heading__icon">
              {/* @ts-ignore */}
              <FaChartBar />
            </div>
            <h3 className="section-heading__title">Бизнес в цифрах</h3>
          </div>
          <p className="section-heading__subtitle">
            Ключевые показатели эффективности платформы в реальных цифрах.
          </p>
        </motion.div>
        <motion.div 
          className="stats__grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          <motion.div className="stat-card" variants={itemVariants}>
            <div className="stat-number">78%</div>
            <div className="stat-label">Ускорение процессов</div>
            <div className="stat-description">Меньше времени на бюрократию и рутину. Автоматическая маршрутизация задач.</div>
          </motion.div>

          <motion.div className="stat-card" variants={itemVariants}>
            <div className="stat-number">99%</div>
            <div className="stat-label">Прозрачность задач</div>
            <div className="stat-description">Ни одна заявка больше не потеряется в чатах. Вы видите весь путь исполнения.</div>
          </motion.div>

          <motion.div className="stat-card" variants={itemVariants}>
            <div className="stat-number">5x</div>
            <div className="stat-label">Рост лояльности (NPS)</div>
            <div className="stat-description">Сотрудники видят, что их проблемы решаются быстро и без лишних вопросов.</div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div 
        className="features__cta"
        initial={{ opacity: 0, scale: 0.95, y: 30 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <h3>Готовы оптимизировать ваши процессы?</h3>
        <p>Начните пилотный проект и убедитесь в эффективности платформы в первый же месяц.</p>
        <button 
          className="btn btn--primary"
          onClick={() => scrollToSection("contact")}
        >
          Запустить пилот
        </button>
      </motion.div>
    </section>
  );
};

export default Features;
